<template>
  <div
    id="save-changes-btn"
    v-tooltip.placementBottom="buttonTooltip"
    data-testid="save-changes-btn"
    :class="buttonClasses"
    @click="handleSaveChanges"
  >
    <div v-if="buttonIcon" class="tw-inline tw-pr-2">
      <i class="icon" :class="buttonIcon"></i>
    </div>
    {{ buttonText }}
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, Ref } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type { SaveButtonState } from '../types'
import { saveButtonFeatures } from '../constants'

const buttonState: Ref<SaveButtonState> = ref('hidden')
const I18n = useI18n()

// eslint-disable-next-line no-undef
defineOptions({
  name: 'SaveChanges',
})

function handleSaveChanges() {
  if (canSend.value) {
    window.eventHub.$emit('saveChanges')
  }
}

function handleEnableSaveChanges() {
  buttonState.value = 'save'
}

function handleSavingChanges() {
  buttonState.value = 'saving'
}

function handleChangesSaved() {
  buttonState.value = 'saved'
}

function handleSaveChangesFailed() {
  buttonState.value = 'failed'
}

const buttonText = computed(() => {
  return I18n.t(saveButtonFeatures[buttonState.value].text)
})

const buttonClasses = computed(() => {
  return saveButtonFeatures[buttonState.value].classes
})

const buttonIcon = computed(() => {
  return saveButtonFeatures[buttonState.value].icon
})

const buttonTooltip = computed(() => {
  const tooltip = saveButtonFeatures[buttonState.value].tooltip
  return tooltip ? I18n.t(tooltip) : null
})

const canSend = computed(() => {
  return buttonState.value === 'save' || buttonState.value === 'failed'
})

onMounted(() => {
  window.eventHub.$on(
    'sf-live-inputs:enableSaveChanges',
    handleEnableSaveChanges
  )
  window.eventHub.$on('sf-live-inputs:savingChanges', handleSavingChanges)
  window.eventHub.$on('sf-live-inputs:savedChanges', handleChangesSaved)
  window.eventHub.$on(
    'sf-live-inputs:saveChangesFailed',
    handleSaveChangesFailed
  )
})

onBeforeUnmount(() => {
  window.eventHub.$off(
    'sf-live-inputs:enableSaveChanges',
    handleEnableSaveChanges
  )
  window.eventHub.$off('sf-live-inputs:savingChanges', handleSavingChanges)
  window.eventHub.$off('sf-live-inputs:savedChanges', handleChangesSaved)
  window.eventHub.$off(
    'sf-live-inputs:saveChangesFailed',
    handleSaveChangesFailed
  )
})
</script>
